// Cycle-16 edit-message bubble. Mirrors the iOS-A / AND-A
// `EditMessageBubble` deltas (Figma `Edit Message` `2869:5218`) — two
// variants: `bubble` (standalone bubble) and `stacked` (rendered inside
// a stacked-message group). Body text is opaque host content and is
// never logged. Edited timestamp is rendered via a deterministic
// `Intl.DateTimeFormat` so snapshots remain stable.

import React from 'react';
import { Linking, Pressable, StyleSheet, Text, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { tokens } from '../design-system/tokens';
import { createTheme } from '../design-system/theme';
import { formatEditedTimestamp } from './messageFormatters';
import { MrkdwnText } from './MrkdwnText';
import { tileMrkdwnPalette } from './tileMrkdwnPalette';
import { decodeBubbleMentions } from './decodeBubbleBody';
import { useTileMaxWidth } from './tileConstraint';
import type { EditMessageVariant } from './EditMessageVariant';

export { EDIT_MESSAGE_VARIANTS, type EditMessageVariant } from './EditMessageVariant';

export interface EditMessageBubbleProps {
  variant: EditMessageVariant;
  isSelf: boolean;
  body: string;
  editedAt: Date;
  onPress?: (() => void) | undefined;
  testID?: string | undefined;
  /** Flat mention id/handle list from the message envelope (for `@handle`
   *  resolution). Optional — ranged mentions are decoded from `body` too. */
  mentions?: ReadonlyArray<string> | undefined;
  /** Tapping a mention pill. */
  onMentionPress?: ((userId: string) => void) | undefined;
  /**
   * When true the tile renders DE-BUBBLED on the neutral app surface (no
   * brand-blue fill), matching every other compact/stacked row. Bubble mode
   * (default) keeps the filled speech bubble.
   */
  compact?: boolean | undefined;
}

export const EditMessageBubble = React.memo<EditMessageBubbleProps>(
  ({ variant, isSelf, body, editedAt, onPress, testID, mentions, onMentionPress, compact }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];
    const theme = createTheme(colorScheme);
    // Thread side panel: cap the bubble to the given width (overrides the 75%).
    const tileMax = useTileMaxWidth();

    // Only a SELF bubble sits on the brand fill → white ink. A peer bubble and
    // BOTH sides in compact/de-bubbled mode sit on the neutral surface, so they
    // use the neutral mrkdwn palette (never white ink, which would vanish).
    const onBrandFill = isSelf && compact !== true;
    const palette = tileMrkdwnPalette(onBrandFill, theme);
    // Ranged mentions from the envelope so multi-word display names render as a
    // single pill (mirrors MessageBubble / StackedMessageRow default paths).
    const knownMentions = decodeBubbleMentions(body);

    const containerStyle = compact === true
      ? styles.compact
      : [
          styles.bubble,
          tileMax !== null ? { maxWidth: tileMax } : null,
          variant === 'bubble' ? styles.bubbleVariant : styles.stackedVariant,
          isSelf
            ? { backgroundColor: colors.accent.brand }
            : { backgroundColor: colors.surface.elevated },
          isSelf ? styles.bubbleSelf : styles.bubbleOther,
        ];

    const bodyColor = onBrandFill ? colors.text.onAccent : colors.text.primary;
    const footerColor = onBrandFill ? 'rgba(255, 255, 255, 0.7)' : colors.text.tertiary;

    const content = (
      <View style={containerStyle} testID={testID}>
        <MrkdwnText
          body={body}
          {...(mentions !== undefined ? { mentions } : {})}
          {...(knownMentions.length > 0 ? { knownMentions } : {})}
          textStyle={{ ...styles.body, color: bodyColor }}
          palette={palette}
          {...(onMentionPress !== undefined ? { onMentionPress } : {})}
          onLinkPress={(url) => {
            void Linking.openURL(url).catch(() => undefined);
          }}
        />
        <Text style={[styles.footer, { color: footerColor }]}>
          {formatEditedTimestamp(editedAt)}
        </Text>
      </View>
    );

    if (!onPress) {
      return content;
    }

    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel="Edited message"
        accessibilityHint="Opens edit history"
      >
        {content}
      </Pressable>
    );
  },
);

EditMessageBubble.displayName = 'EditMessageBubble';

const styles = StyleSheet.create({
  bubble: {
    maxWidth: '75%',
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
  },
  bubbleVariant: {
    borderRadius: tokens.radius.lg,
  },
  stackedVariant: {
    borderRadius: tokens.radius.md,
  },
  bubbleSelf: {
    alignSelf: 'flex-end',
    borderBottomRightRadius: tokens.radius.xs,
  },
  bubbleOther: {
    alignSelf: 'flex-start',
    borderBottomLeftRadius: tokens.radius.xs,
  },
  // De-bubbled compact/stacked tile: no fill, no padding — the body sits
  // full-width on the neutral row surface like every other compact row.
  compact: {
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  body: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.body,
    fontWeight: '400',
    lineHeight: tokens.typography.fontSize.body * tokens.typography.lineHeight.normal,
  },
  footer: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: '400',
    marginTop: tokens.spacing.xxs,
    fontStyle: 'italic',
  },
});
